import React from "react";

import { Button, Col, Modal, Row } from "react-bootstrap";

import { ParticipantTypes, TreeNode, useGetArtistTreeQuery } from "@api/graphql";
import { Loading } from "@components/loading";

import ArtistTree, { TreeNodeWithChildren } from "./ArtistTree";

interface ViewBeneficiaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  rootArtistId: number;
}

const buildTree = (nodes: TreeNode[]): TreeNodeWithChildren[] => {
  const nodeMap: { [key: number]: TreeNodeWithChildren } = {};
  const tree: TreeNodeWithChildren[] = [];

  nodes.forEach((node) => {
    nodeMap[node.node_id] = { ...node, children: [] };
  });

  // Build the tree by associating children with their parents
  nodes.forEach((node) => {
    if (node.depth === 0) {
      tree.push(nodeMap[node.node_id]);
    } else {
      const parent = nodeMap[node.attributes.percentages[0].parent];
      if (parent) {
        parent.children?.push(nodeMap[node.node_id]);
      }
    }
  });

  return tree;
};

const buildCollaborationTree = (rootArtistId: number, nodes: TreeNode[]): TreeNodeWithChildren[] => {
  const filteredNodes = nodes.filter(
    (n) => n.node_id === rootArtistId || (n.parent_nodes && n.parent_nodes.length > 0),
  );
  const nodeMap: { [key: number]: TreeNodeWithChildren } = {};

  // Initialize map
  filteredNodes.forEach((node) => {
    nodeMap[node.node_id] = { ...node, children: [] };
  });

  // Attach children to parents
  filteredNodes.forEach((node) => {
    if (node.parent_nodes.length > 0) {
      node.parent_nodes.forEach((parentId) => {
        const parent = nodeMap[Number(parentId)];
        if (parent) {
          parent.children?.push(nodeMap[node.node_id]);
        }
      });
    }
  });

  // Recursive function to set depth
  const setDepth = (node: TreeNodeWithChildren, depth: number) => {
    node.depth = depth;
    node.children?.forEach((child) => setDepth(child, depth + 1));
  };

  // Find roots (parent_nodes = [])
  const tree = filteredNodes.filter((n) => n.parent_nodes.length === 0).map((root) => nodeMap[root.node_id]);

  // Assign proper depth values
  tree.forEach((root) => setDepth(root, 0));

  return tree;
};

export const ViewBeneficiaryModal = ({ isOpen, onClose, rootArtistId }: ViewBeneficiaryModalProps) => {
  const { data, loading } = useGetArtistTreeQuery({
    variables: { artistId: rootArtistId ? rootArtistId : 0 },
    skip: !rootArtistId,
  });
  const nodes = data?.getArtistTree?.Nodes || [];
  const rootNode = nodes.find((n) => n.node_id === rootArtistId);
  const tree = rootNode?.attributes?.participantType?.includes(ParticipantTypes.COLLABORATION)
    ? buildCollaborationTree(rootArtistId, nodes)
    : buildTree(nodes);

  return (
    <Modal show={isOpen} size="lg" onHide={onClose}>
      <Modal.Header closeButton />
      <Modal.Body className="p-5">
        {loading && <Loading />}
        <Row className="mb-4 h5">Artist beneficiaries</Row>
        {tree.map((node) => (
          <ArtistTree key={node.node_id} node={node} />
        ))}
        <Row className="mt-4 mb-4 text-center">
          <Col>
            <Button type="submit" onClick={onClose}>
              Close
            </Button>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ViewBeneficiaryModal;
